import { createSlice } from '@reduxjs/toolkit';
import { deleteUserAsync, fetchTrainingCertificateListAsync, fetchUserDetailsAsync, registerUserDetailsAsync, resendConsentLinkAsync, updateUserDetailsAsync, verifyFaceRecognitionAsync, verifyPrimaryPhonenumberAsync, verifyUserAsync } from '../thunks/userThunk';
import { BaseUser, Designation } from './rolesSlice';

// User details slice
export interface Address {
  id: number;
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}
export interface PhonenumberStatus {
  message: string;
  exists: boolean
}

export interface FaceRecognitionStatus {
  message: string;
  exists: boolean
}

export interface LastEmployeeDetail {
  id: number;
  roleName: string;
  organizationName: string;
  numberOfYears: string;
}

export interface BankDetails {
  id: number;
  linkedMobileNumber: string;
  accountNumber: string;
  upiId: string;
  panNumber: string;
  bankName: string;
  beneficiaryName: string;
  ifscCode: string;
  consentFileName: string | null,
  passbookFileName: string | null
}

export interface EducationDetail {
  id: number;
  degreeName: string;
  university: string;
  yearOfPassing: string;
  percentage: string;
  educationBoard: string;
}

export interface ProfessionalDetails {
  id: number;
  employeeId: number | null;
  joiningDate: string;
  drivingLicenceNumber: string;
  drivingLicenceDateOfExpiry: string;
}

export interface Department {
  id: number;
  name: string;
  description: string;
}

export interface Group {
  id: number;
  name: string;
  description: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  type: string;
  multipleBranchAccess: boolean;
  specializationMultiMandatory: boolean;
  bankDetailsMandatory: boolean
}

export interface Specialization {
  id: number;
  name: string;
  desc: string;
}

export interface BaseRole {
  department: Department;
  group: Group;
  role: Role;
  type: any; // The type could be more specific, but it's not clear from the provided data
  specializations: Specialization[];
  designation: Designation;
  designationValue: string;
  reportingRoleId: number;
  reportingUserId: number;
}
export interface ConflictDetails {
  conflict: boolean;
  reason: string;
  fromDealer: string;
  toDealer: string;
}


// UserData interface extending BaseUser
export interface UserData extends BaseUser {
  ekycEnabled: boolean;
  loginDetails: ApplicationInfo;
}
export interface EKYCStatus {
  pan: boolean
  bank: boolean
  drivingLicense: boolean
}
export interface ApplicationDetail {
  applicationId: number;
  applicationName: string;
}

export interface Data {
  applicationDetails: ApplicationDetail[];
  userDetails: UserData;
}

export interface TrainingCertificate {
  id: number,
  title: string
}
export interface UserVerification {
  verified: boolean,
  comments: string,
  userNameFromEKyc: string,
}
export interface ApplicationIds {
  id: number
}
export const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  IDLE: "idle",
  FAILED: "failed",
};

export interface StatusMessage {
  message: string
}
export interface ResendConsentMessageStatus {
  message: string,
  smsSent: boolean
}

export interface ApplicationInfo {
  appName: string;
  appId: string;
  lastLogin: string;
  authMethod: string| null;
}

interface UserDetailsState {
  user: UserData | null;
  applicationDetails: ApplicationDetail[] | null;
  loading: boolean;
  error: string | null;
  phoneNumberStatus: PhonenumberStatus | null
  faceRecognitionStatus: FaceRecognitionStatus | null
  status: string;
  verifyPrimaryPhonenumberAPIStatus: string;
  registerUserStatus: string;
  registerUserConflictingStatus: string | null;
  verifyFaceRecognitionAPIStatus: string;
  faceRecognitionErrorMsg: any;
  deleteUserStatus: string;
  statusMessage: StatusMessage | null;
  deleteErrorMsg: any;
  userRegisterErrorMsg: any;
  updateFlowErrorStatus: any;
  updateFlowErrorMessage: any;
  updateUserLoading: any;
  trainingCertificationsList: TrainingCertificate[]
  userVerification: UserVerification | null
  userVerificationStatus: string
  userVerificationErrorMsg: string | null
  resendConsentMessage:ResendConsentMessageStatus | null
  resendConsentMessageStatus:string 
  resendConsentnErrorMsg:string |null

}
const initialUserDetailsState: UserDetailsState = {
  user: null,
  phoneNumberStatus: null,
  applicationDetails: null,
  faceRecognitionStatus: null,
  loading: false,
  error: null,
  status: STATUS.IDLE,
  registerUserStatus: STATUS.IDLE,
  registerUserConflictingStatus:null,
  verifyPrimaryPhonenumberAPIStatus: STATUS.IDLE,
  verifyFaceRecognitionAPIStatus: STATUS.IDLE,
  deleteUserStatus: STATUS.IDLE,
  statusMessage: null,
  faceRecognitionErrorMsg: null,
  deleteErrorMsg: null,
  userRegisterErrorMsg: null,
  updateFlowErrorStatus: null,
  updateFlowErrorMessage: null,
  updateUserLoading: STATUS.IDLE,
  trainingCertificationsList: [],
  userVerification: null,
  userVerificationStatus: STATUS.IDLE,
  userVerificationErrorMsg: null,
  resendConsentMessage: null,
  resendConsentMessageStatus: STATUS.IDLE,
  resendConsentnErrorMsg:null

};

const userDetailsSlice = createSlice({
  name: 'userDetails',
  initialState: initialUserDetailsState,
  reducers: {
    resetPhoneNumberStatus: (state) => {
      state.verifyPrimaryPhonenumberAPIStatus = STATUS.IDLE;
    },
    resetFaceRecognitionStatus: (state) => {
      state.verifyFaceRecognitionAPIStatus = STATUS.IDLE;
    },
    resetUserStatus(state) {
      state.status = STATUS.IDLE;
    },
    resetCameraError(state) {
      state.faceRecognitionErrorMsg = null;
    },
    resetFaceRecignitionStatus(state) {
      state.faceRecognitionStatus = null;
      state.verifyFaceRecognitionAPIStatus = STATUS.IDLE;
    },
    cameraErrorSet(state) {
      state.verifyFaceRecognitionAPIStatus = STATUS.FAILED;
      state.faceRecognitionErrorMsg = "Profile Photo cannot be empty"
    },
    resetRegisterUserStatus(state) {
      state.registerUserStatus = STATUS.IDLE; // Reset status to 'idle'    
    },
    resetDeativateStatus(state) {
      state.deleteUserStatus = STATUS.IDLE;
      state.deleteErrorMsg = null;
    },
    resetUserVerificationStatus(state) {
      state.userVerificationStatus = STATUS.IDLE;
      state.userVerificationErrorMsg = null;
    },
    resetUpdateUserStatus(state) {
      state.updateUserLoading = STATUS.IDLE;
      state.updateFlowErrorMessage = null;
    },
    resetResendConsentSMSStatus(state) {
      state.resendConsentMessageStatus = STATUS.IDLE;
      state.resendConsentMessage = null;
      state.resendConsentnErrorMsg = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error state
      })

      .addCase(fetchUserDetailsAsync.fulfilled, (state, action) => {
        state.user = action.payload.userDetails;
        state.applicationDetails = action.payload.applicationDetails;
        state.loading = false; // Set loading to false when fetching is successful
      })
      .addCase(fetchUserDetailsAsync.rejected, (state, action) => {
        state.loading = false; // Set loading to false when fetching fails
        state.error = action.error.message ?? null; // Update error state
      })
      .addCase(updateUserDetailsAsync.pending, (state) => {
        state.error = null; // Reset error state
        state.updateUserLoading = STATUS.LOADING;
        state.updateFlowErrorMessage = null;

      })
      .addCase(updateUserDetailsAsync.fulfilled, (state, action) => {
        state.user = action.payload.userDetails;
        state.applicationDetails = action.payload.applicationDetails;
        state.updateFlowErrorStatus = STATUS.SUCCESS;
        state.updateFlowErrorMessage = null;
        state.updateUserLoading = STATUS.SUCCESS;
      })
      .addCase(updateUserDetailsAsync.rejected, (state, action) => {
        state.updateFlowErrorStatus = STATUS.FAILED;
        state.updateFlowErrorMessage = action.error.message ?? null; // Update error state
        state.updateUserLoading = STATUS.FAILED;
      })
      .addCase(registerUserDetailsAsync.pending, (state) => {
        state.userRegisterErrorMsg = null; // Reset error state
        state.registerUserStatus = STATUS.LOADING;
      })
      .addCase(registerUserDetailsAsync.fulfilled, (state, action) => {
        state.user = action.payload.userDetails;
        state.applicationDetails = action.payload.applicationDetails;
        state.loading = false; // Set loading to false when fetching is successful
        state.registerUserStatus = STATUS.SUCCESS;
        state.registerUserConflictingStatus = action.payload.userDetails?.conflictDetails;
      })
      .addCase(registerUserDetailsAsync.rejected, (state, action) => {
        state.userRegisterErrorMsg = action.error.message ?? null; // Update error state
        state.registerUserStatus = STATUS.FAILED;
      })
      .addCase(verifyPrimaryPhonenumberAsync.pending, (state) => {
        state.error = null;
        state.verifyPrimaryPhonenumberAPIStatus = STATUS.LOADING;

      })
      .addCase(verifyPrimaryPhonenumberAsync.fulfilled, (state, action) => {
        state.phoneNumberStatus = action.payload;
        state.verifyPrimaryPhonenumberAPIStatus = STATUS.SUCCESS;
      })
      .addCase(verifyPrimaryPhonenumberAsync.rejected, (state, action) => {
        state.error = action.error.message ?? null;
        state.verifyPrimaryPhonenumberAPIStatus = STATUS.FAILED;

      })
      .addCase(deleteUserAsync.pending, (state) => {
        state.deleteUserStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(deleteUserAsync.fulfilled, (state, action) => {
        state.user = action.payload;
        state.deleteUserStatus = STATUS.SUCCESS;
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.deleteUserStatus = STATUS.FAILED;
        state.deleteErrorMsg = action.error.message ?? null;

      })
      .addCase(verifyFaceRecognitionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verifyFaceRecognitionAPIStatus = STATUS.LOADING;

      })
      .addCase(verifyFaceRecognitionAsync.fulfilled, (state, action) => {
        state.faceRecognitionStatus = action.payload;
        state.loading = false;
        state.verifyFaceRecognitionAPIStatus = STATUS.SUCCESS;
      })
      .addCase(verifyFaceRecognitionAsync.rejected, (state, action) => {
        state.loading = false;
        state.faceRecognitionErrorMsg = action.error.message ?? null;
        state.verifyFaceRecognitionAPIStatus = STATUS.FAILED;

      })
      .addCase(fetchTrainingCertificateListAsync.pending, (state) => {
        state.error = null;
      })

      .addCase(fetchTrainingCertificateListAsync.fulfilled, (state, action) => {
        state.trainingCertificationsList = action.payload;
      })
      .addCase(fetchTrainingCertificateListAsync.rejected, (state, action) => {
        state.error = action.error.message ?? null;
      })

      .addCase(verifyUserAsync.pending, (state) => {
        state.userVerificationStatus = STATUS.LOADING;

      })
      .addCase(verifyUserAsync.fulfilled, (state, action) => {
        state.userVerification = action.payload;
        state.userVerificationStatus = STATUS.SUCCESS;
      })
      .addCase(verifyUserAsync.rejected, (state, action) => {
        state.userVerificationErrorMsg = action.error.message ?? null;
        state.userVerificationStatus = STATUS.FAILED;
      })
      .addCase(resendConsentLinkAsync.pending, (state) => {
        state.resendConsentMessageStatus = STATUS.LOADING;
      })
      .addCase(resendConsentLinkAsync.fulfilled, (state, action) => {
        state.resendConsentMessageStatus = STATUS.SUCCESS;
        state.resendConsentMessage = action.payload;

      })
      .addCase(resendConsentLinkAsync.rejected, (state, action) => {
        state.resendConsentMessageStatus = STATUS.FAILED;
        state.resendConsentMessage = null;
        state.resendConsentnErrorMsg = action.error.message ?? null;

      })
  },
});
export const { cameraErrorSet, resetPhoneNumberStatus, resetCameraError, resetFaceRecignitionStatus, resetRegisterUserStatus, resetDeativateStatus, resetFaceRecognitionStatus, resetUserVerificationStatus, resetUpdateUserStatus,resetResendConsentSMSStatus } = userDetailsSlice.actions;
export const { resetUserStatus } = userDetailsSlice.actions;

export default userDetailsSlice.reducer;

